import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { Plus, Check, Pencil, Trash2, Loader2, CalendarClock, Repeat, ChevronDown, Search, X } from "lucide-react";

export const KINDS = [["Bollo", 12], ["Assicurazione", 12], ["Revisione", 24], ["Tagliando", 12], ["Pneumatici", 6]];
const REPEATS = [[0, "Non si ripete"], [6, "Ogni 6 mesi"], [12, "Ogni anno"], [24, "Ogni 2 anni"]];
const EMPTY = { kind: "Bollo", vehicle: "", plate: "", due_date: "", repeat_months: 12, notes: "" };
const fmt = (iso) => iso.split("-").reverse().join("/");
export const daysLeft = (iso) => Math.round((new Date(`${iso}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
export const isAlert = (d) => daysLeft(d.due_date) <= 7;
const repeatLabel = (m) => REPEATS.find(([v]) => v === m)?.[1] || `Ogni ${m} mesi`;

const Badge = ({ n }) => {
  const [txt, cls] = n < 0 ? [`Scaduta da ${-n} g`, "bg-[#E63946] text-white"] : n === 0 ? ["Scade oggi", "bg-[#E63946] text-white"]
    : n <= 7 ? [`Tra ${n} g`, "bg-[#FF9E00] text-black"] : [`Tra ${n} g`, "bg-white/10 text-muted-foreground"];
  return <span className={`text-xs font-bold rounded-full px-2.5 py-1 whitespace-nowrap ${cls}`}>{txt}</span>;
};

const Row = ({ d, onDone, onEdit, onDelete }) => {
  const [busy, setBusy] = useState(false);
  const done = async () => { setBusy(true); await onDone(d); setBusy(false); };
  return (
  <div className={`rounded-xl border bg-[#181A22] p-4 flex flex-wrap items-center gap-3 ${isAlert(d) ? "border-[#E63946]/50" : "border-white/10"}`} data-testid={`deadline-row-${d.id}`}>
    <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
      <div className="font-semibold">{d.kind}</div>
      <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
        <span>Scade il {fmt(d.due_date)}</span>
        {d.repeat_months > 0 && <span className="flex items-center gap-1"><Repeat className="w-3 h-3" />{repeatLabel(d.repeat_months).toLowerCase()}</span>}
      </div>
      {d.notes && <div className="text-xs text-muted-foreground mt-1">{d.notes}</div>}
    </div>
    <Badge n={daysLeft(d.due_date)} />
    <div className="flex items-center gap-1 ml-auto">
      <Button size="sm" onClick={done} disabled={busy} className="rounded-full h-10 bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid={`deadline-done-${d.id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Fatto</Button>
      <button onClick={() => onEdit(d)} className="p-2.5 rounded-full text-muted-foreground hover:text-white hover:bg-white/10" aria-label="Modifica" data-testid={`deadline-edit-${d.id}`}><Pencil className="w-4 h-4" /></button>
      <button onClick={() => onDelete(d)} className="p-2.5 rounded-full text-muted-foreground hover:text-[#F87171] hover:bg-[#E63946]/15" aria-label="Elimina" data-testid={`deadline-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  </div>
  );
};

const Lbl = ({ t, children }) => <label className="block"><span className="text-xs uppercase tracking-wider text-muted-foreground">{t}</span><div className="mt-1">{children}</div></label>;
const field = "h-12 text-base bg-[#181A22] border-white/10";

const DeadlineForm = ({ open, onOpenChange, initial, cars, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial || EMPTY); }, [open, initial]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isStd = KINDS.some(([k]) => k === f.kind);
  const save = async () => {
    if (!f.kind.trim() || !f.vehicle.trim() || !f.due_date) return toast.error("Scrivi tipo, veicolo e data di scadenza");
    setBusy(true);
    try {
      if (f.id) await api.put(`/admin/deadlines/${f.id}`, f); else await api.post("/admin/deadlines", f);
      toast.success("Scadenza salvata");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const vehicles = cars.map((c) => `${c.brand} ${c.model}`.trim());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#12141A] border-white/10 max-w-md max-h-[90vh] overflow-y-auto" data-testid="deadline-form">
        <DialogHeader><DialogTitle className="font-display uppercase">{f.id ? "Modifica scadenza" : "Nuova scadenza"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Lbl t="Cosa scade">
            <div className="flex flex-wrap gap-2">
              {[...KINDS, ["Altro", 0]].map(([k, m]) => {
                const on = k === "Altro" ? !isStd : f.kind === k;
                return <button key={k} type="button" onClick={() => setF((p) => ({ ...p, kind: k === "Altro" ? "" : k, repeat_months: k === "Altro" ? p.repeat_months : m }))} className={`h-10 rounded-full px-4 text-sm border transition-colors ${on ? "bg-white text-black border-white font-semibold" : "border-white/15 text-muted-foreground"}`} data-testid={`deadline-kind-${k.toLowerCase()}`}>{k}</button>;
              })}
            </div>
            {!isStd && <Input value={f.kind} onChange={(e) => set("kind", e.target.value)} placeholder="Es. Controllo freni" className={`${field} mt-2`} data-testid="deadline-kind-custom" />}
          </Lbl>
          <Lbl t="Veicolo">
            <Input value={f.vehicle} onChange={(e) => set("vehicle", e.target.value)} list="deadline-vehicles" placeholder="Scegli dalla flotta o scrivi" className={field} data-testid="deadline-vehicle" />
            <datalist id="deadline-vehicles">{vehicles.map((v) => <option key={v} value={v} />)}</datalist>
          </Lbl>
          <Lbl t="Targa (facoltativa)"><Input value={f.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} className={field} data-testid="deadline-plate" /></Lbl>
          <Lbl t="Data di scadenza"><DateInput value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className={field} data-testid="deadline-date" /></Lbl>
          <Lbl t="Si ripete">
            <div className="grid grid-cols-2 gap-2">
              {REPEATS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => set("repeat_months", v)} className={`h-11 rounded-xl text-sm border ${f.repeat_months === v ? "bg-white text-black border-white font-semibold" : "border-white/15 text-muted-foreground"}`} data-testid={`deadline-repeat-${v}`}>{l}</button>
              ))}
            </div>
          </Lbl>
          <Lbl t="Note (facoltative)"><Input value={f.notes} onChange={(e) => set("notes", e.target.value)} className={field} data-testid="deadline-notes" /></Lbl>
          <Button onClick={save} disabled={busy} className="w-full h-12 rounded-full bg-[#E63946] hover:bg-[#D62828] text-white text-base" data-testid="deadline-save-btn">
            {busy ? <Loader2 className="w-5 h-5 animate-spin mr-1.5" /> : <Check className="w-5 h-5 mr-1.5" />}Salva scadenza
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const groupKey = (d) => (d.plate || d.vehicle.trim().toLowerCase());
const byVehicle = (list) => {
  const map = new Map();
  list.forEach((d) => { const k = groupKey(d); if (!map.has(k)) map.set(k, []); map.get(k).push(d); });
  return [...map.entries()].map(([k, items]) => ({ k, items, first: items[0] })).sort((a, b) => a.first.due_date.localeCompare(b.first.due_date));
};

const VehicleGroup = ({ g, onAdd, autoOpen, ...h }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(!!autoOpen); }, [autoOpen]);
  const due = g.items.filter(isAlert).length;
  const { vehicle, plate } = g.first;
  return (
    <div className={`rounded-2xl border bg-[#12141A] ${due ? "border-[#E63946]/60" : "border-white/10"}`} data-testid={`deadline-group-${g.k}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`deadline-group-toggle-${g.k}`}>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold uppercase truncate">{plate || vehicle}</div>
          <div className="text-xs text-muted-foreground line-clamp-2">{plate ? `${vehicle} · ` : ""}{g.items.length} {g.items.length === 1 ? "scadenza" : "scadenze"} · prossima: {g.first.kind.toLowerCase()} il {fmt(g.first.due_date)}</div>
        </div>
        {due > 0 ? <span className="text-xs font-bold rounded-full px-2.5 py-1 bg-[#E63946] text-white whitespace-nowrap" data-testid={`deadline-group-due-${g.k}`}>{due} da fare</span> : <Badge n={daysLeft(g.first.due_date)} />}
        <ChevronDown className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2" data-testid={`deadline-group-list-${g.k}`}>
          {g.items.map((d) => <Row key={d.id} d={d} {...h} />)}
          <Button variant="outline" onClick={() => onAdd({ ...EMPTY, vehicle, plate })} className="w-full h-11 rounded-full border-dashed border-white/25 bg-transparent" data-testid={`deadline-group-add-${g.k}`}><Plus className="w-4 h-4 mr-1.5" />Aggiungi scadenza</Button>
        </div>
      )}
    </div>
  );
};

export const DeadlinesTab = ({ cars, onChange }) => {
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/deadlines").then((r) => { setList(r.data); onChange?.(r.data); }).catch(() => setList([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const openForm = (d) => { setEdit(d); setOpen(true); };
  const onDone = async (d) => {
    try {
      const { data } = await api.post(`/admin/deadlines/${d.id}/done`);
      toast.success(data.next ? `Fatto! Prossima ${d.kind.toLowerCase()} il ${fmt(data.next.due_date)}` : "Fatto!");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const onDelete = async (d) => {
    if (!window.confirm(`Eliminare la scadenza ${d.kind} di ${d.vehicle}?`)) return;
    await api.delete(`/admin/deadlines/${d.id}`).then(() => { toast.success("Scadenza eliminata"); load(); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };
  const h = { onDone, onEdit: openForm, onDelete };
  const all = list || [];
  const [q, setQ] = useState("");
  const norm = (s) => (s || "").toLowerCase().replace(/[\s-]/g, "");
  const groups = byVehicle(all).filter((g) => !q.trim() || norm(g.first.plate).includes(norm(q)) || norm(g.first.vehicle).includes(norm(q)));
  const single = q.trim() && groups.length === 1;
  return (
    <div data-testid="deadlines-tab" className="space-y-6">
      <div className="flex items-center gap-3">
        <CalendarClock className="w-6 h-6 text-[#E63946]" />
        <p className="text-sm text-muted-foreground flex-1">Bolli, assicurazioni, revisioni, tagliandi e gomme. Ti avviso qui 7 giorni prima e il giorno stesso.</p>
        <Button onClick={() => openForm(null)} className="rounded-full h-11 bg-[#E63946] hover:bg-[#D62828] text-white shrink-0" data-testid="deadline-add-btn"><Plus className="w-4 h-4 mr-1.5" />Nuova</Button>
      </div>
      {list === null ? <Loader2 className="w-6 h-6 animate-spin text-[#E63946]" /> : all.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center" data-testid="deadlines-empty">Nessuna scadenza. Tocca <b>Nuova</b> per aggiungere la prima.</p>
      ) : (
        <div className="space-y-2">
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca targa o veicolo" autoCapitalize="characters" className="pl-9 pr-10 h-12 text-base bg-[#181A22] border-white/10" data-testid="deadlines-search" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground" aria-label="Cancella ricerca" data-testid="deadlines-search-clear"><X className="w-4 h-4" /></button>}
          </div>
          {groups.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="deadlines-search-empty">Nessun veicolo trovato per "{q}".</p>}
          {groups.map((g) => <VehicleGroup key={g.k} g={g} onAdd={openForm} autoOpen={single} {...h} />)}
        </div>
      )}
      <DeadlineForm open={open} onOpenChange={setOpen} initial={edit} cars={cars} onSaved={load} />
    </div>
  );
};
